// Dashboard Page Component
import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  Chip,
  useTheme,
  CircularProgress,
  Alert,
  Tooltip as MuiTooltip,
  FormControl,
  Select,
  MenuItem,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  IconButton,
  Button,
  useMediaQuery,
  alpha,
} from "@mui/material";
import {
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  MedicalServices as MedicalServicesIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
  MoreVert as MoreVertIcon,
  History as HistoryIcon,
  CalendarMonth as CalendarIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Group as GroupIcon,
  Print as PrintIcon,
  Dashboard as DashboardIcon,
} from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useTenant } from "../contexts/TenantContext";
import { useResidents } from "../hooks/useResidents";

import { useAuth } from "../contexts/AuthContext";
import OrderMedicationService from "../services/OrderMedicationService";
import residentService from "../services/residentService";
import clinicalNoteService from "../services/clinicalNoteService";
import { MedicationOrderDTO } from "../types/medication.types";
import { formatToUIDate, formatToUIDateTimeLong, formatToUITime } from "../utils/dateUtils";
import AttendanceWidget from "../components/AttendanceWidget";
import { useNavigate } from "react-router-dom";
import ResidentAvatar from "../components/ResidentAvatar";
import appointmentService from "../services/appointmentService";
import { MedicalInformation as DoctorIcon } from "@mui/icons-material";

const COLORS = ["#0077b6", "#00b4d8", "#90e0ef", "#caf0f8"];

const getFacilityDisplayName = (note: any, fallbackFacilityId?: string, fallbackFacilityName?: string) => {
  return (
    note?.facilityName ||
    note?.facility?.name ||
    fallbackFacilityName ||
    note?.facilityId ||
    note?.facility_id ||
    fallbackFacilityId ||
    "Facility"
  );
};

const getResidentDisplayName = (note: any, residents: any[]) => {
  const direct =
    note?.residentName ||
    note?.resident?.name ||
    note?.residentFullName ||
    note?.resident_full_name;
  if (direct) return String(direct);

  const noteResidentId =
    note?.residentId ||
    note?.resident_id ||
    note?.resident?.id;

  const resident = residents.find((r) => String(r.id) === String(noteResidentId));
  if (resident) return `${resident.firstName} ${resident.lastName}`.trim();

  return "N/A";
};

// ─── Styled Components Shorthands ──────────────────────────────────────────

const StatCard = ({ title, value, icon, color, loading, onClick }: any) => (
  <Card
    elevation={0}
    onClick={onClick}
    sx={{
      p: 2.5,
      borderRadius: "16px",
      border: "1px solid",
      borderColor: "divider",
      background: "#fff",
      height: "100%",
      display: "flex",
      alignItems: "center",
      transition: "all 0.2s ease-in-out",
      cursor: onClick ? "pointer" : "default",
      "&:hover": {
        boxShadow: onClick ? `0 4px 16px ${alpha(color, 0.15)}` : 'none',
        borderColor: onClick ? alpha(color, 0.5) : 'divider'
      },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box
        sx={{
          p: 1.25,
          borderRadius: "12px",
          background: `${color}20`,
          color: color,
          display: "flex",
        }}
      >
        {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 28 } })}
      </Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 800, color: color, lineHeight: 1, fontSize: "1.75rem" }}>
          {loading ? <CircularProgress size={20} /> : value}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, fontSize: "0.82rem", whiteSpace: "nowrap" }}>
          {title}
        </Typography>
      </Box>
    </Box>
  </Card>
);

const SectionHeader = ({ title, icon }: any) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
    <Box sx={{ color: "primary.main", display: "flex" }}>
      {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 20 } })}
    </Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary" }}>
      {title}
    </Typography>
  </Box>
);

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { tenant, currentTenantId } = useTenant();
  const { user: authUser } = useAuth();

  // ─── Logic Block (Preserved) ──────────────────────────────────────────────

  const {
    residents,
    loading: residentsLoading,
    error: residentsError,
    totalElements: totalResidents,
    fetchResidents,
    fetchResidentsByFacility,
  } = useResidents();




  const [recentAdmissions, setRecentAdmissions] = useState<any[]>([]);
  const [activeResidentCount, setActiveResidentCount] = useState<number | null>(null);
  const [dashboardTotalResidents, setDashboardTotalResidents] = useState<number | null>(null);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [statusData, setStatusData] = useState<any[]>([]);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const [medicationOrderCount, setMedicationOrderCount] = useState<number | null>(null);
  const [activeMedicationCount, setActiveMedicationCount] = useState<number | null>(null);
  const [cancelledMedicationCount, setCancelledMedicationCount] = useState<number | null>(null);
  const [loadingMeds, setLoadingMeds] = useState(false);

  const [expiringOrders, setExpiringOrders] = useState<MedicationOrderDTO[]>([]);
  const [loadingExpiring, setLoadingExpiring] = useState(false);

  const [refillEndingSoonList, setRefillEndingSoonList] = useState<any[]>([]);
  const [loadingRefillList, setLoadingRefillList] = useState(false);

  const [medicationOrdersEnding, setMedicationOrdersEnding] = useState<any[]>([]);
  const [loadingEnding, setLoadingEnding] = useState(false);

  const [progressNotes, setProgressNotes] = useState<any[]>([]);
  const [noteDays, setNoteDays] = useState(7);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [todayAppointments, setTodayAppointments] = useState<any[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [printingId, setPrintingId] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Prioritize logged in user's facility ID (UUID) over tenant context
  const loggedInUserStr = localStorage.getItem('user');
  const loggedInUser = loggedInUserStr ? JSON.parse(loggedInUserStr) : null;
  const userFacility = Array.isArray(loggedInUser?.facility) ? loggedInUser.facility[0] : (loggedInUser?.facility || null);
  const userFacilityId = userFacility?.id || loggedInUser?.facilityId || loggedInUser?.tenantId;

  // Use the currently selected facility from tenant context first (supports facility switching)
  const facilityId =
    currentTenantId ||
    tenant?.id ||
    userFacilityId;

  useEffect(() => {
    // Utility to check if ID is likely a valid UUID vs a mock string like 'localhost'
    const isValidId = (id: any) => id && id.length > 10 && id.includes('-');



    if (isValidId(facilityId)) {

      setLoadingExpiring(true);
      OrderMedicationService.getExpiringOrdersByFacility(facilityId)
        .then((orders) => setExpiringOrders(orders))
        .finally(() => setLoadingExpiring(false));

      setLoadingRefillList(true);
      OrderMedicationService.getRefillEndingSoon(facilityId)
        .then((list) => setRefillEndingSoonList(list))
        .finally(() => setLoadingRefillList(false));

      setLoadingEnding(true);
      OrderMedicationService.getMedicationOrdersEnding(facilityId)
        .then((list) => setMedicationOrdersEnding(list))
        .catch((err) => console.error("Ending list error", err))
        .finally(() => setLoadingEnding(false));

      setLoadingAppointments(true);
      appointmentService.getTodayAppointments(facilityId)
        .then((data) => setTodayAppointments(data))
        .catch((err) => console.error("Appointments error", err))
        .finally(() => setLoadingAppointments(false));
    }

    if (isValidId(facilityId)) {
      setLoadingRecent(true);
      setLoadingStatus(true);

      residentService.getRecentAdmissions(facilityId as string)
        .then((data: any) => setRecentAdmissions(data))
        .catch((err) => console.error("Recent admissions error", err))
        .finally(() => setLoadingRecent(false));

      // aliases = exact normalised keys the API returns (lowercase, no separators)
      const statusColorMap = [
        { key: "active", aliases: ["active"], label: "Active", color: "#00B4D8" },
        { key: "discharged", aliases: ["discharged"], label: "Discharged", color: "#cbd5e1" },
        { key: "out_of_nursing_home", aliases: ["outofhome", "outofnursinghome", "outofnursing"], label: "Out of Facility", color: "#fbbf24" },
        { key: "transferred", aliases: ["transfer", "transferred"], label: "Transferred", color: "#60a5fa" },
        { key: "hospital_appointment", aliases: ["hospitalappointment"], label: "Hospital Appointment", color: "#a78bfa" },
        { key: "with_family", aliases: ["withfamily"], label: "With Family", color: "#f472b6" },
      ];

      // Helper: try dashboard-counts endpoint; fall back to individual status calls if empty/fails
      const loadStatusCounts = async () => {
        try {
          const counts = await residentService.getDashboardCounts(facilityId as string);

          // The new API returns { totalResidents: N, activeMedicationOrders: N, residentStatusCounts: {...} }
          if (counts.totalResidents !== undefined) setDashboardTotalResidents(counts.totalResidents);
          if (counts.activeMedicationOrders !== undefined) setActiveMedicationCount(counts.activeMedicationOrders);

          const statusCountsObj = counts.residentStatusCounts || counts; // Fallback if unwrapped

          // Normalise all response keys: lowercase + strip separators/spaces
          const norm: Record<string, number> = {};
          if (statusCountsObj && typeof statusCountsObj === 'object') {
            Object.entries(statusCountsObj).forEach(([k, v]) => {
              if (typeof v === 'number') {
                norm[k.toLowerCase().replace(/[_\s-]/g, "")] = v;
              }
            });
          }

          // Check key against its bare form, camelCase variant, AND known API aliases
          const findCount = (key: string, aliases: string[]) => {
            const bare = key.replace(/[_\s-]/g, "");
            const camel = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase()).toLowerCase();
            // Try bare, camel, then each alias
            const candidates = [bare, camel, ...aliases];
            for (const c of candidates) {
              if (norm[c] !== undefined) return norm[c];
            }
            return 0;
          };

          const chartData = statusColorMap.map(({ key, aliases, label, color }) => ({
            name: label,
            value: findCount(key, aliases),
            color,
          }));

          const totalFromApi = chartData.reduce((s, d) => s + d.value, 0);

          if (totalFromApi > 0) {
            // Dashboard-counts endpoint returned real data — use it
            setActiveResidentCount(findCount("active", ["active"]));
            setStatusData(chartData);
            return;
          }
        } catch (_) {
          // Endpoint not available — fall through to individual calls
        }

        // Fallback: individual getResidentsByStatus calls.
        // 'discharged' is excluded — it is now served by dashboard-counts only.
        const results = await Promise.all(
          statusColorMap
            .filter(({ key }) => key !== "discharged")
            .map(({ key, label, color }) =>
              residentService.getResidentsByStatus(facilityId as string, key)
                .then(list => ({ name: label, value: list.length, color }))
                .catch(() => ({ name: label, value: 0, color }))
            )
        );

        // Re-attach discharged with 0 (its real value comes from dashboard-counts above)
        const fullResults = statusColorMap.map(({ label, color }) => {
          const found = results.find(r => r.name === label);
          return found ?? { name: label, value: 0, color };
        });

        setActiveResidentCount(fullResults.find(r => r.name === "Active")?.value ?? 0);
        setStatusData(fullResults);
      };

      loadStatusCounts()
        .catch(err => console.error("Status counts error", err))
        .finally(() => setLoadingStatus(false));
    }
  }, [facilityId, fetchResidentsByFacility, fetchResidents]);

  useEffect(() => {
    const isValidId = (id: any) => id && id.length > 10 && id.includes('-');
    if (isValidId(facilityId)) {
      setLoadingNotes(true);
      clinicalNoteService.getProgressNotesByFacility(facilityId as string, noteDays)
        .then((notes: any[]) => {
          const sortedNotes = [...notes].sort((a, b) => new Date(b.dateTime || b.createdAt).getTime() - new Date(a.dateTime || a.createdAt).getTime());
          setProgressNotes(sortedNotes);
        })
        .catch((err) => {
          console.error("Failed to fetch progress notes:", err);
          setProgressNotes([]);
        })
        .finally(() => setLoadingNotes(false));
    }
  }, [facilityId, noteDays]);

  const handlePrintClinicDocument = async (residentId: string) => {
    if (!residentId) return;

    if (printingId) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      setPrintingId(null);
      if (printingId === residentId) return;
    }

    setPrintingId(residentId);
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const blob = await residentService.getClinicDocumentPdf(residentId);
      if (controller.signal.aborted) return;

      const url = window.URL.createObjectURL(blob);
      const iframe = document.createElement("iframe");
      iframe.style.visibility = "hidden";
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "1px";
      iframe.style.height = "1px";
      iframe.style.border = "0";
      iframe.src = url;
      document.body.appendChild(iframe);

      const cleanup = () => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
        window.URL.revokeObjectURL(url);
      };

      iframe.onload = () => {
        if (controller.signal.aborted) {
          cleanup();
          return;
        }

        setPrintingId(null);
        abortControllerRef.current = null;

        try {
          if (iframe.contentWindow) {
            iframe.contentWindow.focus();
            // Use onafterprint to cleanup immediately after dialog is dismissed
            iframe.contentWindow.onafterprint = cleanup;
            iframe.contentWindow.print();
          }
        } catch (printErr) {
          console.error("Direct print failed:", printErr);
          cleanup();
        }
      };

      // Increase safety timeout to 15 minutes to prevent premature closing of print preview in Chrome
      setTimeout(cleanup, 900000);

    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log("Print request cancelled");
      } else {
        console.error("Error printing clinic document:", err);
      }
      setPrintingId(null);
      abortControllerRef.current = null;
    }
  };

  const groupedProgressNotes = progressNotes.reduce((acc: Record<string, any[]>, note: any) => {
    const key = String(note?.facilityId || note?.facility_id || facilityId || "facility");
    if (!acc[key]) acc[key] = [];
    acc[key].push(note);
    return acc;
  }, {});

  const activeResidents = residents.filter((r) => r.status === "ACTIVE").length;
  const loading = residentsLoading || loadingMeds;

  if (loading && residents.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={40} thickness={4} />
      </Box>
    );
  }

  // ─── Render Block (Redesigned) ──────────────────────────────────────────

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", px: 0, pb: 2, pt: 3, }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: isMobile ? 2 : 0 }}>
          <Box sx={{ typography: 'h1', display: 'flex', alignItems: 'center' }}>
            <DashboardIcon sx={{ fontSize: 36, color: "#48CAE4" }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight="700"
              sx={{ mb: 0.5, color: "#1A2B45" }}
            >
              {tenant?.name?.replace(/_/g, " ") || "Facility Overview"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.85rem" }}>
              Management Dashboard • {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </Typography>
          </Box>
        </Box>
        {/* <Stack direction="row" spacing={1.5}>
          <Chip
            icon={<HistoryIcon sx={{ fontSize: "16px !important" }} />}
            label="Real-time Feed"
            variant="outlined"
            size="small"
            sx={{ borderRadius: "8px", fontWeight: 600, px: 0.5, height: 36 }}
          />
        </Stack> */}
      </Box>

      {residentsError && (
        <Alert severity="error" sx={{ mb: 4, borderRadius: "12px" }}>
          {residentsError}
        </Alert>
      )}

      {/* Key Stats Grid */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4} md={4}>
          <StatCard
            title="Total Residents"
            value={dashboardTotalResidents !== null ? dashboardTotalResidents : totalResidents}
            icon={<PeopleIcon />}
            color="#00B4D8"
            loading={residentsLoading}
            onClick={() => navigate('/residents', { state: { filterStatus: 'all' } })}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <StatCard
            title="Active Residents"
            value={activeResidentCount !== null ? activeResidentCount : activeResidents}
            icon={<CheckCircleIcon />}
            color="#34d399"
            loading={residentsLoading}
            onClick={() => navigate('/residents', { state: { filterStatus: 'active' } })}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <StatCard
            title="Active Medication Orders"
            value={activeMedicationCount || 0}
            icon={<MedicalServicesIcon />}
            color="#fbbf24"
            loading={loadingMeds}
            onClick={authUser?.role?.includes('ADMIN') ? () => navigate('/medication-orders', { state: { statusFilter: 'ACTIVE' } }) : null}
          />
        </Grid>
      </Grid>

      {/* Main Content Layout */}
      <Grid container spacing={2}>
        {/* Left Column: Alerts & Status */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={4}>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr" },
              gap: 4
            }}>
              {/* Attendance Check-In / Check-Out */}
              <AttendanceWidget facilityId={facilityId} />
              {/* Resident Status Pie Chart */}
              <Card elevation={0} sx={{ p: 3, borderRadius: "20px", border: "1px solid", borderColor: "divider", height: '100%' }}>
                <SectionHeader title="Resident Status" icon={<GroupIcon sx={{ fontSize: 20 }} />} />
                {loadingStatus ? (
                  <Box sx={{ height: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Box sx={{ height: 320, display: "flex", flexDirection: "column" }}>
                    <Box sx={{ flex: 1, width: "100%", minHeight: 0 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={
                              statusData.some(s => s.value > 0)
                                ? statusData.filter(s => s.value > 0)
                                : [{ name: "No Data", value: 1, color: "#e2e8f0" }]
                            }
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={85}
                            paddingAngle={5}
                            dataKey="value"
                            nameKey="name"
                          >
                            {(statusData.some(s => s.value > 0)
                              ? statusData.filter(s => s.value > 0)
                              : [{ name: "No Data", value: 1, color: "#e2e8f0" }]
                            ).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                    <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 2 }}>
                      {statusData.map((s) => (
                        <Box key={s.name} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.color }} />
                          <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                            {s.name}: {s.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Card>
            </Box>

            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr" },
              gap: 4
            }}>
              {/* Recent Admissions List */}
              <Card elevation={0} sx={{ p: 3, borderRadius: "20px", border: "1px solid", borderColor: "divider", height: '100%' }}>
                <SectionHeader title="Recent Admissions" icon={<ArrowForwardIcon sx={{ fontSize: 20 }} />} />
                <Stack spacing={2.5}>
                  {loadingRecent ? (
                    <CircularProgress size={20} />
                  ) : recentAdmissions.length > 0 ? (
                    recentAdmissions.slice(0, 5).map((resident) => (
                      <Box
                        key={resident.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                          p: 1,
                          borderRadius: "8px",
                          mx: -1
                        }}
                        onClick={() => navigate("/clinical-notes", { state: { residentId: resident.id, view: "facesheet" } })}
                      >
                        <ResidentAvatar
                          residentId={resident.id}
                          residentName={`${resident.firstName} ${resident.lastName}`}
                          fallbackUrl={resident.profilePictureUrl}
                          sx={{ bgcolor: "primary.light", color: "primary.main", fontWeight: 700 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {resident.firstName} {resident.lastName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            Room {resident.roomNumber || "N/A"} • {formatToUIDate(resident.admissionDate)}
                          </Typography>
                        </Box>
                        <Chip label="New" size="small" variant="outlined" sx={{ borderRadius: "6px", height: 20, fontSize: "0.65rem", fontWeight: 800 }} />
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ color: "text.disabled", fontStyle: "italic" }}>No recent records</Typography>
                  )}
                </Stack>
              </Card>

              {/* Expiring (8 Days) List */}
              <Card elevation={0} sx={{ p: 3, borderRadius: "20px", border: "1px solid", borderColor: "divider", height: '100%' }}>
                <SectionHeader title="Expiring in 8 Days" icon={<AccessTimeIcon sx={{ fontSize: 20 }} />} />
                <Stack spacing={2}>
                  {loadingExpiring ? (
                    <CircularProgress size={20} />
                  ) : expiringOrders.slice(0, 5).length > 0 ? (
                    expiringOrders.slice(0, 5).map((order: any, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 1.5,
                          borderRadius: "12px",
                          bgcolor: "rgba(245, 158, 11, 0.05)",
                          border: "1px solid rgba(245, 158, 11, 0.1)"
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{order.medicationName}</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">For: {order.residentName}</Typography>
                        <Typography variant="caption" sx={{ color: "#d97706", fontWeight: 600 }}>Expires: {formatToUIDate(order.medicationExpirationDate || order.endDate)}</Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ color: "text.disabled", fontStyle: "italic" }}>No items are expiring</Typography>
                  )}
                </Stack>
              </Card>
            </Box>
          </Stack>
        </Grid>

        {/* Right Column: Medications & Notes */}
        <Grid item xs={12} lg={8}>
          <Stack spacing={4}>
            {/* Medication Alerts Grid - Box grid to prevent double-spacing/alignment issues */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
              width: "100%"
            }}>
              <Card elevation={0} sx={{ p: 3, borderRadius: "20px", border: "1px solid", borderColor: "divider", height: 420, display: 'flex', flexDirection: 'column' }}>
                <SectionHeader title="Appointments Due in 48 Hours" icon={<DoctorIcon sx={{ fontSize: 20 }} />} />
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  {loadingAppointments ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                  ) : todayAppointments.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead sx={{
                          "& .MuiTableCell-head": {
                            color: "#434445ff !important",
                          }
                        }}>
                          <TableRow>
                            <TableCell>Date & Time</TableCell>
                            <TableCell >Resident</TableCell>
                            <TableCell >Hospital Name</TableCell>
                            <TableCell >Doctor</TableCell>
                            <TableCell>Appointment Type</TableCell>
                            <TableCell >Reason</TableCell>
                            <TableCell >Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {todayAppointments.slice(0, 10).map((apt: any, idx) => {
                            // Resident Name Mapping
                            const lookupResId = apt.residentId || apt.resident_id || apt.patientId;
                            const lookupRes = residents.find(r => String(r.id) === String(lookupResId));
                            const resName =
                              apt.fullName ||
                              apt.residentFullName ||
                              apt.residentName ||
                              (apt.residentFirstName && apt.residentLastName ? `${apt.residentFirstName} ${apt.residentLastName}` : null) ||
                              (apt.firstName && apt.lastName ? `${apt.firstName} ${apt.lastName}` : null) ||
                              (lookupRes ? `${lookupRes.firstName} ${lookupRes.lastName}` : null) ||
                              apt.resident?.fullName ||
                              (apt.resident?.firstName && apt.resident?.lastName ? `${apt.resident.firstName} ${apt.resident.lastName}` : null) ||
                              apt.resident?.name ||
                              apt.patientName ||
                              "N/A";

                            // Doctor Name Mapping
                            const docName =
                              apt.doctorName ||
                              apt.doctor ||
                              apt.attendingPhysicianName ||
                              apt.doctorDetails?.name ||
                              apt.doctor?.name ||
                              apt.doctorId ||
                              apt.userId ||
                              "N/A";

                            // Hospital Name Mapping
                            const hospName =
                              apt.hospitalName ||
                              (typeof apt.hospital === "string" ? apt.hospital : null) ||
                              apt.hospital?.name ||
                              apt.hospitalDetails?.name ||
                              apt.facilityName ||
                              "N/A";

                            return (
                              <TableRow
                                key={idx}
                                hover
                                sx={{
                                  cursor: 'pointer',
                                  '&:last-child td': { border: 0 }
                                }}
                                onClick={() => navigate("/clinical-notes", { state: { residentId: lookupResId, view: "medical-appointment" } })}
                              >
                                <TableCell >
                                  {formatToUIDate(apt.appointmentDate)} {formatToUITime(apt.appointmentTime)}
                                </TableCell>
                                <TableCell >
                                  {resName}
                                </TableCell>
                                <TableCell >
                                  {hospName}
                                </TableCell>
                                <TableCell >
                                  {docName}
                                </TableCell>
                                <TableCell >
                                  {apt.appointmentType || "N/A"}
                                </TableCell>
                                <TableCell >
                                  {apt.reason || "N/A"}
                                </TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                  <MuiTooltip title="Print Clinic Document" arrow>
                                    <IconButton
                                      size="small"
                                      sx={{ color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.08), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) } }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePrintClinicDocument(lookupResId);
                                      }}
                                    >
                                      {printingId === lookupResId ? (
                                        <CircularProgress size={18} color="inherit" />
                                      ) : (
                                        <PrintIcon sx={{ fontSize: 18 }} />
                                      )}
                                    </IconButton>
                                  </MuiTooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" sx={{ color: "text.disabled", textAlign: 'center', py: 4 }}>
                      No appointments for today
                    </Typography>
                  )}
                </Box>
              </Card>

              <Card elevation={0} sx={{ p: 3, borderRadius: "20px", border: "1px solid", borderColor: "divider", height: 420, display: 'flex', flexDirection: 'column' }}>
                <SectionHeader title="Refills Due" icon={<CalendarIcon sx={{ fontSize: 20 }} />} />
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  <Stack spacing={2}>
                    {loadingRefillList ? <CircularProgress size={20} /> : refillEndingSoonList.slice(0, 4).length > 0 ? (
                      refillEndingSoonList.slice(0, 4).map((order: any, idx) => (
                        <Box key={idx} sx={{ p: 1.5, borderRadius: "12px", bgcolor: "rgba(59, 130, 246, 0.05)", border: "1px solid rgba(59, 130, 246, 0.1)", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <Box>
                            <Typography variant="caption" display="block" color="text.secondary">Resident Name: <strong>{order.residentName}</strong></Typography>
                            <Typography variant="caption" display="block" color="text.secondary">Medication: <strong>{order.medicationName || "Medication Refill"}</strong></Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500, whiteSpace: "nowrap" }}>Due: {formatToUIDate(order.endDate)}</Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ color: "text.disabled" }}>No refills due</Typography>
                    )}
                  </Stack>
                </Box>
              </Card>
            </Box>

            {/* Progress Notes Table */}
            <Card elevation={0} sx={{ borderRadius: "20px", border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
              <Box sx={{ p: 3, display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, gap: { xs: 2, sm: 0 }, borderBottom: "1px solid", borderColor: "divider" }}>
                <SectionHeader title="Clinical Progress Notes" icon={<AssignmentTurnedInIcon sx={{ fontSize: 20 }} />} />
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => navigate("/resident-progress-notes")}
                    sx={{ fontWeight: 700, fontSize: "0.75rem", textTransform: "none" }}
                  >
                    View All
                  </Button>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 16, alignSelf: "center" }} />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>Showing Last</Typography>
                    <FormControl size="small">
                      <Select
                        value={noteDays}
                        onChange={(e) => setNoteDays(Number(e.target.value))}
                        sx={{ height: 32, borderRadius: "8px", fontSize: "0.75rem", fontWeight: 700, bgcolor: "grey.50" }}
                      >
                        {[3, 7, 14, 30].map((d) => <MenuItem key={d} value={d} sx={{ fontSize: "0.75rem", fontWeight: 700 }}>{d} Days</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ overflowX: "auto", width: "100%" }}>
                <TableContainer sx={{ maxHeight: 400, minWidth: 600 }}>
                  <Table stickyHeader size="small">
                    <TableHead sx={{
                      "& .MuiTableCell-head": {
                        color: "#434445ff !important",


                      }
                    }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, py: 2, bgcolor: "#fff" }}>Resident</TableCell>
                        <TableCell sx={{ fontWeight: 700, py: 2, bgcolor: "#fff" }}>Timestamp</TableCell>
                        <TableCell sx={{ fontWeight: 700, py: 2, bgcolor: "#fff" }}>Clinical Note</TableCell>
                        <TableCell sx={{ fontWeight: 700, py: 2, bgcolor: "#fff" }}>Provider</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loadingNotes ? (
                        <TableRow><TableCell colSpan={4} align="center" sx={{ py: 8 }}><CircularProgress size={24} /></TableCell></TableRow>
                      ) : progressNotes.length > 0 ? (
                        Object.entries(groupedProgressNotes).flatMap(([facilityKey, notes], facilityIdx) => {
                          const facilityLabel = getFacilityDisplayName(
                            (notes && notes.length > 0) ? notes[0] : null,
                            facilityKey,
                            tenant?.name?.replace(/_/g, " "),
                          );
                          const rows: React.ReactNode[] = [
                            <TableRow key={`facility-${facilityKey}`} sx={{ bgcolor: "grey.50" }}>
                              {/* <TableCell colSpan={4} sx={{ py: 1.5, fontWeight: 800, color: "text.primary" }}>
                                {facilityLabel}
                              </TableCell> */}
                            </TableRow>
                          ];

                          (notes as any[]).forEach((note, idx) => {
                            rows.push(
                              <TableRow
                                key={`${facilityKey}-${idx}`}
                                hover
                                sx={{ "&:last-child td": { border: 0 }, cursor: "pointer" }}
                                onClick={() => {
                                  const noteResidentId = note?.residentId || note?.resident_id || note?.resident?.id;
                                  if (noteResidentId) {
                                    navigate("/resident-progress-notes", { state: { residentId: noteResidentId } });
                                  }
                                }}
                              >
                                <TableCell >
                                  {getResidentDisplayName(note, residents)}
                                </TableCell>
                                <TableCell >
                                  {formatToUIDateTimeLong(note.dateTime || note.noteDate || note.createdAt || note.createdOn)}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 300 }}>
                                  <MuiTooltip title={note.note} arrow placement="top">
                                    <Typography variant="body2">
                                      {note.note}
                                    </Typography>
                                  </MuiTooltip>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    maxWidth: 150,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                  }}
                                >
                                  {note.authorName || note.createdByName || "Staff"}
                                </TableCell>
                              </TableRow>
                            );
                          });

                          // Add a little separation between facilities when more than one group exists
                          if (facilityIdx < Object.keys(groupedProgressNotes).length - 1) {
                            rows.push(
                              <TableRow key={`facility-gap-${facilityKey}`}>
                                <TableCell colSpan={4} sx={{ p: 0, borderBottom: 0 }}>
                                  <Box sx={{ height: 6, bgcolor: "transparent" }} />
                                </TableCell>
                              </TableRow>
                            );
                          }

                          return rows;
                        })
                      ) : (
                        <TableRow><TableCell colSpan={4} align="center" sx={{ py: 8, color: "text.disabled" }}>No clinical notes found.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Card>
          </Stack>
        </Grid>
      </Grid>

    </Box>
  );
};

export default DashboardPage;